import { useEffect } from 'react';
import { getSEO } from './seoConfig';
import { useI18n } from '../i18n';
import { ROUTE_SLUGS } from '../i18n/routes';
import { LIVE_LOCALES } from '../i18n/locales';

interface SEOProps {
  /** Eski (legacy) path key. Örn: "/play", "/education", "/daily" — seoConfig anahtarı. */
  path: string;
  /** Title override. */
  title?: string;
  /** Description override. */
  description?: string;
  /** TR/EN slug'ın sonuna eklenecek dinamik kısım. Örn: "/x-wing" → /egitim/x-wing + /en/education/x-wing */
  pathSuffix?: string;
  /** Bu sayfaya özgü ekstra JSON-LD (Article, BreadcrumbList vb.). */
  extraJsonLd?: object | object[];
}

const SITE = 'https://sudokuarea.com';



function setDocumentTitle(val: string) {
  document.title = val;
}

function setDocumentLang(val: string) {
  document.documentElement.lang = val;
}

function setCanonical(href: string) {
  const existing = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]'));
  if (existing.length > 0) {
    existing[0].setAttribute('href', href);
    for (let i = 1; i < existing.length; i++) {
      existing[i].remove();
    }
  } else {
    const link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', href);
    document.head.appendChild(link);
  }
}

interface HreflangEntry {
  lang: string;
  href: string;
}

function setHreflangs(entries: HreflangEntry[]) {
  const existing = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="alternate"][hreflang]'));
  existing.forEach(el => el.remove());
  for (const entry of entries) {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', entry.lang);
    link.setAttribute('href', entry.href);
    document.head.appendChild(link);
  }
}

function setMetaTag(attrName: 'name' | 'property', attrVal: string, content: string | undefined) {
  const selector = `meta[${attrName}="${attrVal}"]`;
  const existing = Array.from(document.querySelectorAll<HTMLMetaElement>(selector));
  if (content === undefined) {
    existing.forEach(el => el.remove());
    return;
  }
  if (existing.length > 0) {
    existing[0].setAttribute('content', content);
    for (let i = 1; i < existing.length; i++) {
      existing[i].remove();
    }
  } else {
    const meta = document.createElement('meta');
    meta.setAttribute(attrName, attrVal);
    meta.setAttribute('content', content);
    document.head.appendChild(meta);
  }
}

function setRobots(noindex: boolean) {
  const existing = Array.from(document.querySelectorAll<HTMLMetaElement>('meta[name="robots"]'));
  if (noindex) {
    if (existing.length > 0) {
      existing[0].setAttribute('content', 'noindex, nofollow');
      for (let i = 1; i < existing.length; i++) existing[i].remove();
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      meta.setAttribute('content', 'noindex, nofollow');
      document.head.appendChild(meta);
    }
  } else {
    existing.forEach(el => el.remove());
  }
}

function setJsonLd(items: object[]) {
  const existing = Array.from(document.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]'));
  existing.forEach(el => el.remove());
  for (const item of items) {
    const script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.textContent = JSON.stringify(item);
    document.head.appendChild(script);
  }
}

/**
 * Locale-aware SEO bileşeni.
 *
 * Deterministik DOM senkronizasyonu:
 * Prerender sırasında DOM'a temiz tek set yazar (Playwright snapshot alır).
 * İstemcide ilk açılışta ve SPA geçişlerinde DOM'daki etiketleri günceller,
 * duplikasyon (2 canonical, 20 hreflang vb.) veya bayat etiket bırakmaz.
 */
export function SEO({ path, title, description, pathSuffix, extraJsonLd }: SEOProps) {
  const { locale } = useI18n();
  const cfg = getSEO(path);

  // Dile özel override (i18n) → geriye uyum (titleEn/descriptionEn, sadece 'en') → TR fallback.
  const over = cfg.i18n?.[locale];
  const finalTitle =
    title ?? over?.title ?? (locale === 'en' ? cfg.titleEn : undefined) ?? cfg.title;
  const finalDesc =
    description ?? over?.description ?? (locale === 'en' ? cfg.descriptionEn : undefined) ?? cfg.description;

  const suffix = pathSuffix ? (pathSuffix.startsWith('/') ? pathSuffix : `/${pathSuffix}`) : '';

  const pathForLocale = (loc: typeof locale) => {
    if (!cfg.routeKey) return path;
    const base = ROUTE_SLUGS[loc][cfg.routeKey];
    return `${base === '/' ? '' : base}${suffix}` || '/';
  };

  const canonicalPath = pathForLocale(locale);
  const canonical = `${SITE}${canonicalPath === '/' ? '/' : canonicalPath}`;
  const og = cfg.ogImage ?? `${SITE}/og-default.png`;
  const OG_LOCALE_MAP: Record<string, string> = {
    tr: 'tr_TR', en: 'en_US', nl: 'nl_NL', fr: 'fr_FR', de: 'de_DE',
    it: 'it_IT', pl: 'pl_PL', ru: 'ru_RU', es: 'es_ES',
  };
  const ogLocale = OG_LOCALE_MAP[locale] ?? 'en_US';

  const jsonLdItems: object[] = [];
  if (cfg.jsonLd) {
    const ld = typeof cfg.jsonLd === 'function' ? cfg.jsonLd(locale) : cfg.jsonLd;
    if (Array.isArray(ld)) jsonLdItems.push(...ld);
    else jsonLdItems.push(ld);
  }
  if (extraJsonLd) {
    if (Array.isArray(extraJsonLd)) jsonLdItems.push(...extraJsonLd);
    else jsonLdItems.push(extraJsonLd);
  }

  const showHreflang = !cfg.noindex && cfg.routeKey != null;
  const trPath = pathForLocale('tr');

  const jsonLdSerialized = JSON.stringify(jsonLdItems);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    // 1. Document title & HTML lang
    setDocumentTitle(finalTitle);
    setDocumentLang(locale);

    // 2. Canonical URL (tam 1 adet)
    setCanonical(canonical);

    // 3. Robots (noindex)
    setRobots(Boolean(cfg.noindex));

    // 4. Hreflang (showHreflang ise tam 10 adet: 9 dil + x-default, değilse 0)
    if (showHreflang) {
      const hreflangs: HreflangEntry[] = [];
      for (const loc of LIVE_LOCALES) {
        const p = pathForLocale(loc);
        if (p) {
          hreflangs.push({ lang: loc, href: `${SITE}${p === '/' ? '/' : p}` });
        }
      }
      if (trPath) {
        hreflangs.push({ lang: 'x-default', href: `${SITE}${trPath === '/' ? '/' : trPath}` });
      }
      setHreflangs(hreflangs);
    } else {
      setHreflangs([]);
    }

    // 5. Meta description
    setMetaTag('name', 'description', finalDesc);

    // 6. Open Graph
    setMetaTag('property', 'og:type', 'website');
    setMetaTag('property', 'og:site_name', 'Sudoku Area');
    setMetaTag('property', 'og:locale', ogLocale);
    setMetaTag('property', 'og:title', finalTitle);
    setMetaTag('property', 'og:description', finalDesc);
    setMetaTag('property', 'og:url', canonical);
    setMetaTag('property', 'og:image', og);

    // 7. Twitter Card
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', finalTitle);
    setMetaTag('name', 'twitter:description', finalDesc);
    setMetaTag('name', 'twitter:image', og);

    // 8. Structured Data JSON-LD
    setJsonLd(jsonLdItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    finalTitle,
    finalDesc,
    locale,
    canonical,
    cfg.noindex,
    showHreflang,
    trPath,
    ogLocale,
    og,
    jsonLdSerialized,
  ]);

  return null;
}
